import angular from 'angular';
import messages from 'angular-messages';
import sanitize from 'angular-sanitize';
import uuid from '../uuid';
import indeterminateProgress from '../indeterminate-progress';
import pagination from '../pagination';
import menuButton from '../menu-button';
import i18n from '../i18n';
import utils from '../utils';

import tableDirective from './table-directive';
import tableTemplateService from './table-template-service';

/**
 * @ngdoc module
 * @name akamai.components.table
 *
 * @guideline https://design.akamai.com/components/table/
 *
 * @requires module:ngSanitize
 * @requires module:akamai.components.uuid
 * @requires module:akamai.components.indeterminate-progress
 * @requires module:akamai.components.pagination
 * @requires module:akamai.components.menu-button
 * @requires module:akamai.components.i18n
 * @requires module:akamai.components.utils
 *
 * @description
 * The table control uses labeled rows and columns used to display a collection of structured data.
 * Rows alternate color for readability; this allows the user to follow a row without confusing
 * it with another. Row content can include text, numbers, links, buttons, and images. The table
 * control can be adapted to specific needs by enabling and disabling functionality.
 *
 * NOTE: Starting in akamai-core 1.4.0, you can handle all sorting, filtering, pagination, etc.
 * on the server side by using the on-context-change and context attributes.
 *
 * @example table.html
 * {% raw %}
 * <akam-table items="rows"
 *   id-property="custom-id-field"
 *   on-change="myRowSelectionCallback(selectedItems)"
 *   selected-items="selectedItems">
 *   <akam-table-toolbar>
 *     <span>Custom toolbar markup here</span>
 *     <i class="my-awesome-icon"></i>
 *   </akam-table-toolbar>
 *   <akam-table-row>
 *     <akam-table-column row-property="id" header-name="my.id">
 *       <span>CUSTOM MARKUP</span>{{ row.id }}
 *     </akam-table-column>
 *   <akam-table-column row-property="name" not-filterable not-sortable header-name="my.name">
 *     {{ row.name }} is awesome
 *   </akam-table-column>
 *   <akam-table-column row-property="street" header-name="my.street"></akam-table-column>
 *   <akam-table-column class="column-action" header-name="my.actions">
 *    <akam-menu-button></akam-menu-button>
 *   </akam-table-column>
 *   </akam-table-row>
 * </akam-table>
 * {% endraw %}
 *
 * @example table-server.html
 * {% raw %}
 * <akam-table items="rows"
 *   id-property="custom-id-field"
 *   on-context-change="contextChanged(context)"
 *   selected-items="selectedItems">
 *   <akam-table-row>
 *    <akam-table-column row-property="street" header-name="my.street"></akam-table-column>
 *   </akam-table-row>
 * </akam-table>
 * {% endraw %}
 */
export default angular.module('akamai.components.table', [
  sanitize,
  messages,
  uuid.name,
  indeterminateProgress.name,
  pagination.name,
  menuButton.name,
  i18n.name,
  utils.name
])

/**
 * @ngdoc directive
 * @name akamTable
 * @description Creates a table control. NOTE: If you are using an `<akam-menu-button>`
 * inside of a table row, then you must put the class ".column-action" on the
 * `<akam-table-column>` in order to achieve UXD approved behavior. See the table example
 * for a usage example.
 *
 * @restrict E
 *
 * @param {Object[]} items Required data model for the table.
 *
 * @param {string} [id-property='id'] Attribute to use as an ID for the row. This will be used
 * to keep track of selected items, as well as used as the `track by` clause in our ng-repeat.
 * The ID must be unique per row.
 *
 * @param {function(Object[])} [on-select] Optional callback that will be called whenever an item
 * is selected. It is passed the entire array of selected items.
 *
 * @param {function(Context)} [on-context-change] Optional callback and marker attribute.  Used to
 * enable server side handling.  Will be called whenever the client side table context is updated.
 * The context can be updated in one of 4 ways:
 *
 * <ol>
 *   <li>User changes page number.</li>
 *   <li>User updates page size.</li>
 *   <li>User clicks on a sortable column header to produce results in ascending or descending
 *   order.</li>
 *   <li>User enters a search term in the search/filter box.</li>
 * </ol>
 *
 * The function is passed a `Context` (see context property below for schema).
 *
 * @param {Object} [context] Optional double bound object which contains information about the
 * context/state of the table, reflecting the sorted column (if any), sort direction, number of
 * total items, page size, page number and filter text. Useful for client and server handling, for
 * example: setting the default page size to 25, 50 or defaulting to a different page number.
 *
 * @param {Number} [context.totalItems=0] The total number of items to show the user.
 *
 * @param {Number} [context.pageSize=10] The number of items to show per page.
 *
 * @param {Number} [context.pageNumber=1] The page number selected in the table.
 *
 * @param {String} [context.filter] The text entered into the filter.
 *
 * @param {String} [context.sortColumn] The name of the column to be sorted.
 *
 * @param {String} [context.sortDirection] The direction which to sort the contents (limited to
 * 'asc' and 'desc').
 *
 * @param {Number} [filter-delay=0] The number of milliseconds before we allow for a change
 * of context to be debounced.
 * <b>NOTE: This attribute will work if the on-context-change method has been set.</b>
 *
 * @param {function(Object[])} [on-change] Optional callback that will be called whenever the list
 * of visible rows changes. It is passed the array of rows. This is especially useful if a user
 * needs to make a second, more expensive, AJAX request in order to fetch some more data about the
 * list of visible rows. <b>NOTE: This method does not get called if the on-context-change method
 * has been set.</b>
 *
 * @param {function(Object, String)} [on-filter] Optional callback that will be called whenever the
 * filter is updated. It is passed the item in the items array and the search term in the filter.
 * This is especially useful if a developer wants to filter the table results in addition to the
 * filter text available.  Example usage: a custom drop down could be added to the toolbar and only
 * items that match the selected value in the drop down should show.
 * <b>NOTE: This method does not get called if the on-context-change method has been set.</b>
 *
 * @param {Object[]} [selected-items] Optional array of pre-selected rows. Will be two-way data
 * bound so only this or the `on-change` callback is necessary in order to have selectable rows.
 *
 * @param {*} [not-pageable] The presence of this attribute determines whether the pagination is on/off.
 * If it isn't set then the pagination is assumed to be on.
 * If it is set but its value is undefined then the pagination will be turned off.
 * If it is set and its value is defined then the activation will be determined based on this value.
 *
 * @param {*} [not-sortable] The presence of this attribute will disable sorting for the entire
 * table.
 *
 * @param {*} [not-filterable] The presence of this attribute will disable filtering for the entire
 * table. It will have the effect of hiding the filter input box.
 *
 * @param {*} [preserve-filter] By default, every time akam-table data changes, filter input box is
 * cleared and table content doesn't preserve previous filtering. By specifying this attribute
 * filter input box preserves previous value and new data is immediately filtered according to it.
 *
 * @param {string} [no-items-message] Optional string which is the message that will be displayed
 * when there is no data to display.
 *
 * @param {string} [no-filter-results-message|TranslateKey] Optional string which is the message
 * that will be displayed when there is no data displayed based upon some filter.
 *
 * @param {string|TranslateKey} [filter-placeholder] The placeholder for the filter input.
 *
 * @param {string|TranslateKey} [item-failure-message] The message shown when an error occurs while
 * loading table data.
 *
 * @param {HTMLElement} akam-table-column A column definition element. This is used to declare
 * the state and behavior of a table column. Also, any markup within `<akam-table-column>` will be
 * used as custom markup for the column's cells.
 *
 * @param {string} akam-table-column.row-property The property used to render the column for each
 * row in the table.
 *
 * @param {string|TranslateKey} [akam-table-column.header-name] The column header text.
 *
 * @param {function(Object): string|number|Date} [akam-table-column.on-sort] The column will be
 * sorted by the value returned by this function. This is used to sort the column by a value other
 * than `row-property`. It is passed a row object.
 *
 * @param {function(Object): string|number|Date} [akam-table-column.on-sort-comparator] An optional
 * comparator function that will be passed to angular's orderBy method. See the "comparator" option
 * at https://docs.angularjs.org/api/ng/filter/orderBy.
 *
 * @param {*} [akam-table-column.default-sort] If present, the column will be initially sorted and
 * ordered by its string value. If default-sort='desc', then the column will be initially sorted in
 * descending order. Otherwise, it will be initially sorted in ascending order.
 *
 * @param {*} [akam-table-column.not-sortable] The presence of this attribute will disable sorting
 * for the column.
 *
 * @param {*} [akam-table-column.not-filterable] The presence of this attribute will disable
 * filtering for the column.
 *
 * @param {String} [failure-message] Optional string which is the message that will be
 * displayed when data fails to load.
 *
 * @param {String} [default-sort] Optional attribute for akam-table-column for that column to be
 * initially sorted and ordered by its string value, if used as default-sort='desc', then initial
 * sorted column will be in descending order, otherwise, it will be always ascending order.
 *
 * @param {*} [enable-large-pages] The presence of this attribute will allow a custom page size of
 * '100' in the page size dropdown.

 * @param {*} [enable-small-pages] The presence of this attribute will allow a custom page size of
 * '5' in the page size dropdown.
 */
  .directive('akamTable', tableDirective)

/**
 * @name akamTableTemplate
 *
 * @description
 * Minimal service to get the inner template, representing the `<table>` element for
 * an akam-data-table.
 */
  .factory('akamTableTemplate', tableTemplateService);
